import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Add from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useAppSelector } from "@/app/store";
import { SIDEBAR_COLLAPSED_WIDTH_PX, SIDEBAR_EXPANDED_WIDTH_PX } from "@/constants";
import { useBrandDisplay } from "@/features/settings/hooks/useBranding";
import { selectAccessibleTabs } from "@/features/users/model/usersSlice";
import { NAV_TABS, type NavTab } from "@/shared/ui/layout/navTabs";
import { useSidebarCollapse } from "@/shared/ui/layout/useSidebarCollapse";

const SETTINGS_TAB = NAV_TABS.find((navTab) => navTab.name === "settings");

function SidebarNavItem({ tab, expanded }: { tab: NavTab; expanded: boolean }) {
  const { t } = useTranslation("common");
  const match = useMatch({ path: tab.route, end: tab.matchEnd });
  const Icon = tab.icon;
  const label = t(tab.labelKey);

  return (
    <Tooltip title={label} placement="right" disableHoverListener={expanded}>
      <ListItemButton
        component={NavLink}
        to={tab.route}
        end={tab.matchEnd}
        sx={{
          borderRadius: 1,
          minHeight: 44,
          px: expanded ? 1.25 : 0.75,
          justifyContent: expanded ? "flex-start" : "center",
          ...(match
            ? {
                color: "var(--theme-text-primary)",
                bgcolor: "var(--theme-overlay-olive-15)",
                boxShadow: "inset 4px 0 0 var(--theme-primary)",
                "&:hover": {
                  bgcolor: "var(--theme-overlay-light-10)",
                  color: "var(--theme-text-primary)",
                  boxShadow: "inset 4px 0 0 var(--theme-primary)",
                },
              }
            : {
                color: "var(--theme-text-secondary)",
                bgcolor: "transparent",
                "&:hover": {
                  bgcolor: "var(--theme-hover-secondary)",
                  color: "var(--theme-text-primary)",
                },
              }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: expanded ? 40 : 0,
            mr: expanded ? 1 : 0,
            justifyContent: "center",
            color: "inherit",
          }}
        >
          <Icon fontSize="small" />
        </ListItemIcon>
        {expanded ? (
          <ListItemText
            primary={label}
            slotProps={{
              primary: {
                noWrap: true,
                sx: { typography: "body2", fontWeight: 500, color: "inherit" },
              },
            }}
          />
        ) : null}
      </ListItemButton>
    </Tooltip>
  );
}

export function AppSidebar() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const { forceCollapsed } = useSidebarCollapse();
  const [userExpanded, setUserExpanded] = useState(true);
  const expanded = !forceCollapsed && userExpanded;
  const { brandName, iconSrc, fullLogoSrc } = useBrandDisplay();
  // Keyed by source, so a newly saved image gets its own chance to load after an earlier one failed.
  const [failedIconSrc, setFailedIconSrc] = useState<string | null>(null);
  const [failedFullLogoSrc, setFailedFullLogoSrc] = useState<string | null>(null);
  const iconFailed = failedIconSrc === iconSrc;
  // Expanded shows the full logo on its own; collapsed never does. One that will not load falls
  // back to the icon and the name as text rather than to an empty header.
  const shownFullLogoSrc =
    expanded && fullLogoSrc !== null && failedFullLogoSrc !== fullLogoSrc ? fullLogoSrc : null;
  const isNewChatRoute = location.pathname === "/chat";

  const accessibleTabs = useAppSelector(selectAccessibleTabs);

  // Render the main list in the backend-provided order, mapping each allowed
  // tab name to its repo-side route/icon/label config.
  const mainTabs = useMemo(
    () =>
      accessibleTabs
        .map((tabName) => NAV_TABS.find((navTab) => navTab.name === tabName))
        .filter(
          (navTab): navTab is NavTab =>
            navTab !== undefined && navTab.placement === "main",
        ),
    [accessibleTabs],
  );

  const showSettings = accessibleTabs.some((tabName) => tabName === "settings");
  const canChat = accessibleTabs.some((tabName) => tabName === "chats");

  const toggleExpanded = useCallback(() => {
    setUserExpanded((v) => !v);
  }, []);

  const handleNewChat = useCallback(() => {
    navigate("/chat", { replace: true, state: { resetAt: Date.now() } });
  }, [navigate]);

  const widthPx = expanded ? SIDEBAR_EXPANDED_WIDTH_PX : SIDEBAR_COLLAPSED_WIDTH_PX;

  return (
    <Box
      component="nav"
      aria-label={t("sidebar.ariaLabel")}
      sx={{
        position: "relative",
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: widthPx,
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        background: "var(--theme-gradientBackground)",
        borderRight: "1px solid var(--theme-sidebar-border)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          flexShrink: 0,
          minHeight: 64,
          display: "flex",
          alignItems: "center",
          px: expanded ? 1.5 : 0.5,
          pr: expanded ? 4 : 2.5,
          borderBottom: "1px solid var(--theme-sidebar-border)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: 1,
            justifyContent: expanded ? "flex-start" : "center",
          }}
        >
          {shownFullLogoSrc !== null ? (
            <Box
              component="img"
              src={shownFullLogoSrc}
              alt={brandName}
              onError={() => {
                setFailedFullLogoSrc(shownFullLogoSrc);
              }}
              sx={{
                height: 40,
                minWidth: 0,
                maxWidth: "100%",
                objectFit: "contain",
                objectPosition: "left center",
              }}
            />
          ) : (
            <>
              {iconFailed ? (
                <Box
                  aria-hidden
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    border: "1px solid var(--theme-sidebar-border)",
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={iconSrc}
                  alt=""
                  onError={() => {
                    setFailedIconSrc(iconSrc);
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    objectFit: "contain",
                  }}
                />
              )}
              {expanded ? (
                <Typography
                  component="span"
                  variant="subtitle1"
                  noWrap
                  title={brandName}
                  sx={{ fontWeight: 600, color: "var(--theme-text-primary)" }}
                >
                  {brandName}
                </Typography>
              ) : null}
            </>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-label={expanded ? t("sidebar.collapse") : t("sidebar.expand")}
          sx={{
            position: "absolute",
            right: expanded ? 4 : -12,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: "var(--theme-overlay-light-10)",
            color: "var(--theme-text-secondary)",
            "&:hover": {
              bgcolor: "var(--theme-overlay-light-20)",
              color: "var(--theme-text-primary)",
            },
          }}
        >
          {expanded ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
        </IconButton>
      </Box>

      <Divider
        sx={{
          borderColor: "var(--theme-sidebar-border)",
          "&::before, &::after": { borderColor: "var(--theme-sidebar-border)" },
        }}
      />

      {canChat ? (
        <Box sx={{ px: expanded ? 1 : 0.5, pt: 1.5, pb: 0.5 }}>
          <Tooltip title={t("sidebar.newChat")} placement="right" disableHoverListener={expanded}>
            <Button
              onClick={handleNewChat}
              fullWidth={expanded}
              aria-label={t("sidebar.newChat")}
              sx={{
                minHeight: 40,
                borderRadius: 1,
                justifyContent: expanded ? "center" : "center",
                px: expanded ? 1.5 : 0.75,
                minWidth: expanded ? undefined : 44,
                bgcolor: isNewChatRoute ? "var(--theme-primary-dark, var(--theme-primary))" : "var(--theme-primary)",
                color: "var(--theme-text-on-primary)",
                textTransform: "none",
                fontWeight: 500,
                boxShadow: isNewChatRoute ? "inset 0 0 0 1px var(--theme-overlay-light-20)" : "none",
                "&:hover": {
                  bgcolor: "var(--theme-hover-primary, var(--theme-primary-light))",
                },
              }}
            >
              {expanded ? `+ ${t("sidebar.newChat")}` : <Add fontSize="small" />}
            </Button>
          </Tooltip>
        </Box>
      ) : null}

      <List
        dense
        component="div"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          py: 1,
          px: expanded ? 1 : 0.5,
        }}
      >
        {mainTabs.map((tab) => (
          <SidebarNavItem key={tab.name} tab={tab} expanded={expanded} />
        ))}
      </List>

      {showSettings && SETTINGS_TAB !== undefined ? (
        <Box
          sx={{
            flexShrink: 0,
            borderTop: "1px solid var(--theme-sidebar-border)",
            py: 1,
            px: expanded ? 1 : 0.5,
          }}
        >
          <SidebarNavItem tab={SETTINGS_TAB} expanded={expanded} />
        </Box>
      ) : null}
    </Box>
  );
}
